import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { ProfitCenter } from '@/lib/models/profitCenter';
import type { ProfitCenterIntegration } from '@/lib/models/profitCenterIntegration';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per l'endpoint dei ProfitCenters
 */
export interface ProfitCenterEndpoints {
	/**
	 * Recupera la lista dei centri di profitto con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=name asc")
	 * @returns Promise con response OData contenente array di ProfitCenter
	 */
	getProfitCenters(query: string): Promise<FetchResponse<ODataResponse<ProfitCenter>>>;

	/**
	 * Recupera un singolo centro di profitto per ID
	 * @param id - ID del centro di profitto
	 * @returns Promise con singolo ProfitCenter
	 */
	getProfitCenter(id: number): Promise<FetchResponse<ProfitCenter>>;

	/**
	 * Aggiorna un centro di profitto esistente
	 * @param id - ID del centro di profitto da aggiornare
	 * @param payload - Dati da aggiornare (il backend ignora id e metadata)
	 * @returns Promise con ProfitCenter aggiornato
	 */
	updateProfitCenter(id: number, payload: ProfitCenter): Promise<FetchResponse<ProfitCenter>>;

	/**
	 * Crea un nuovo centro di profitto
	 * @param payload - Dati del nuovo centro di profitto (il backend ignora id e metadata)
	 * @returns Promise con ProfitCenter creato
	 */
	postProfitCenter(payload: ProfitCenter): Promise<FetchResponse<ProfitCenter>>;

	/**
	 * Elimina un centro di profitto
	 * @param id - ID del centro di profitto da eliminare
	 * @returns Promise con response di conferma
	 */
	deleteProfitCenter(id: number): Promise<FetchResponse<void>>;

	/**
	 * Recupera le integrazioni per un centro di profitto
	 * @param id - ID del centro di profitto
	 * @returns Promise con response OData contenente array di ProfitCenterIntegration
	 */
	getIntegrations(id: number): Promise<FetchResponse<ODataResponse<ProfitCenterIntegration>>>;
}

/**
 * Implementazione tipizzata degli endpoint per i ProfitCenters
 */
const profitCenterEndpoints: ProfitCenterEndpoints = {
	getProfitCenters(query: string): Promise<FetchResponse<ODataResponse<ProfitCenter>>> {
		return api.get(`/odata/v1/ProfitCenters${query}`);
	},

	getProfitCenter(id: number): Promise<FetchResponse<ProfitCenter>> {
		return api.get(`/odata/v1/ProfitCenters/${id}`);
	},

	updateProfitCenter(id: number, payload: ProfitCenter): Promise<FetchResponse<ProfitCenter>> {
		return api.put(`/api/v1/ProfitCenters/${id}`, payload);
	},

	postProfitCenter(payload: ProfitCenter): Promise<FetchResponse<ProfitCenter>> {
		return api.post('/api/v1/ProfitCenters', payload);
	},

	deleteProfitCenter(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/ProfitCenters/${id}`);
	},

	getIntegrations(id: number): Promise<FetchResponse<ODataResponse<ProfitCenterIntegration>>> {
		return api.get(`/odata/v1/ProfitCenterIntegrations?$filter=(sourceId eq ${id})`);
	},
};

export default profitCenterEndpoints;
